import React, { useState, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import SkillsInput from './SkillsInput';
import MonthPicker from './MonthPicker';
import '../../../../styles/profile/components/add-experience-modal.css';

interface WorkExperience {
  uid: string;
  employer_name: string;
  job_title: string;
  skills: string[] | null;
  description: string | null;
  achievements: string[] | null;
  start_date: string;
  end_date: string | null;
  location: string | null;
  is_current: boolean;
}

interface AddExperienceModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: ExperienceData) => void;
  title?: string;
  employerLabel?: string;
  jobTitleLabel?: string;
  initialData?: WorkExperience | null;
  isEditMode?: boolean;
}

interface ExperienceData {
  uid?: string;
  employer: string;
  jobTitle: string;
  space: string;
  role: string;
  startDate: string;
  endDate: string;
  location: string;
  isCurrentPosition: boolean;
  skills: string[];
  description: string;
  achievements: string[];
}

export default function AddExperienceModal({ 
  open, 
  onClose, 
  onAdd, 
  title = "Add Work Experience",
  employerLabel = "Employer",
  jobTitleLabel = "Job Title",
  initialData = null,
  isEditMode = false
}: AddExperienceModalProps) {
  const [employer, setEmployer] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [location, setLocation] = useState('');
  const [isCurrentPosition, setIsCurrentPosition] = useState(false);
  const [skills, setSkills] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [achievements, setAchievements] = useState<string[]>([]);
  
  const overlayRef = useRef<HTMLDivElement>(null);

  const isFormValid = employer.trim() !== '' && jobTitle.trim() !== '';

  useEffect(() => {
    if (open && initialData) {
      setEmployer(initialData.employer_name || '');
      setJobTitle(initialData.job_title || '');
      setStartDate(initialData.start_date ? new Date(initialData.start_date) : null);
      setEndDate(initialData.end_date ? new Date(initialData.end_date) : null);
      setLocation(initialData.location || '');
      setIsCurrentPosition(initialData.is_current || false);
      setSkills(initialData.skills || []);
      setDescription(initialData.description || '');
      setAchievements(initialData.achievements || []);
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) {
      setEmployer('');
      setJobTitle('');
      setStartDate(null);
      setEndDate(null);
      setShowStartCalendar(false);
      setShowEndCalendar(false);
      setLocation('');
      setIsCurrentPosition(false);
      setSkills([]);
      setDescription('');
      setAchievements([]);
    }
  }, [open]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ select date';
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const handleSubmit = () => {
    if (!employer.trim() || !jobTitle.trim()) {
      return;
    }

    const data: ExperienceData = {
      employer,
      jobTitle,
      space: '',
      role: '',
      startDate: startDate ? startDate.toISOString() : '',
      endDate: endDate ? endDate.toISOString() : '',
      location,
      isCurrentPosition,
      skills,
      description,
      achievements: achievements.filter(a => a.trim() !== ''),
    };

    if (isEditMode && initialData?.uid) {
      data.uid = initialData.uid;
    }

    onAdd(data);
  };

  const handleAddAchievement = () => {
    setAchievements([...achievements, '']);
  };

  const handleRemoveAchievement = (index: number) => {
    setAchievements(achievements.filter((_, i) => i !== index));
  };

  const handleAchievementChange = (index: number, value: string) => {
    const newAchievements = [...achievements];
    newAchievements[index] = value;
    setAchievements(newAchievements);
  };

  if (!open) return null;

  return (
    <div
      className="add-experience-modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="add-experience-modal">
        <div className="add-experience-modal-header">
          <h2 className="add-experience-modal-title">{title}</h2>
        </div>

        <div className="add-experience-modal-body">
          <div className="add-experience-modal-grid">
            <div className="add-experience-modal-field">
              <label className="add-experience-modal-label">
                {employerLabel}
                <span className="add-experience-modal-required">Required</span>
              </label>
              <input
                type="text"
                className="add-experience-modal-input"
                placeholder="i.e. Google, Tesla, etc"
                value={employer}
                onChange={(e) => setEmployer(e.target.value)}
              />
            </div>

            <div className="add-experience-modal-field">
              <label className="add-experience-modal-label">Skills</label>
              <SkillsInput skills={skills} onChange={setSkills} placeholder="Select Skills" />
            </div>
          </div>

          <div className="add-experience-modal-field-full">
            <label className="add-experience-modal-label">
              {jobTitleLabel}
              <span className="add-experience-modal-required">Required</span>
            </label>
            <input
              type="text"
              className="add-experience-modal-input"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
          </div>

          <div className="add-experience-modal-field-full">
            <label className="add-experience-modal-label">Description</label>
            <textarea
              className="add-experience-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="add-experience-modal-field-full">
            <label className="add-experience-modal-label">Achievements</label>
            {achievements.map((achievement, index) => (
              <div key={index} className="add-experience-modal-achievement-item">
                <input
                  type="text"
                  className="add-experience-modal-input"
                  placeholder="Achievement"
                  value={achievement}
                  onChange={(e) => handleAchievementChange(index, e.target.value)}
                />
                <button
                  type="button"
                  className="add-experience-modal-remove-btn"
                  onClick={() => handleRemoveAchievement(index)}
                  aria-label="Remove achievement"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="add-experience-modal-add-achievement"
              onClick={handleAddAchievement}
            >
              <span className="add-experience-modal-add-icon">+</span>
            </button>
          </div>

          <div className="add-experience-modal-grid">
            <div className="add-experience-modal-field">
              <label className="add-experience-modal-label">Start Date</label>
              <div className="add-experience-modal-date-wrapper">
                <button
                  type="button"
                  className="add-experience-modal-date-btn"
                  style={{ color: startDate ? '#1f2937' : '#9ca3af' }}
                  onClick={() => {
                    setShowStartCalendar(!showStartCalendar);
                    setShowEndCalendar(false);
                  }}
                >
                  {formatDate(startDate)}
                </button>
                {showStartCalendar && (
                  <MonthPicker
                    selectedDate={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setShowStartCalendar(false);
                    }}
                    onClose={() => setShowStartCalendar(false)}
                    maxDate={endDate || undefined}
                  />
                )}
              </div>
            </div>

            <div className="add-experience-modal-field">
              <label className="add-experience-modal-label">End Date</label>
              <div className="add-experience-modal-date-wrapper">
                <button
                  type="button"
                  className="add-experience-modal-date-btn"
                  style={{ color: endDate ? '#1f2937' : '#9ca3af' }}
                  onClick={() => {
                    if (!isCurrentPosition) {
                      setShowEndCalendar(!showEndCalendar);
                      setShowStartCalendar(false);
                    }
                  }}
                  disabled={isCurrentPosition}
                >
                  {formatDate(endDate)}
                </button>
                {showEndCalendar && !isCurrentPosition && (
                  <MonthPicker
                    selectedDate={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setShowEndCalendar(false);
                    }}
                    onClose={() => setShowEndCalendar(false)}
                    minDate={startDate || undefined}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="add-experience-modal-field-full">
            <label className="add-experience-modal-label">Location</label>
            <input
              type="text"
              className="add-experience-modal-input"
              placeholder="+ add location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div className="add-experience-modal-field-full">
            <label className="add-experience-modal-toggle-label">
              <span>Is current position?</span>
              <div
                className={`add-experience-modal-toggle ${isCurrentPosition ? 'add-experience-modal-toggle--active' : ''}`}
                onClick={() => setIsCurrentPosition(!isCurrentPosition)}
              >
                <div className="add-experience-modal-toggle-thumb" />
              </div>
            </label>
          </div>
        </div>

        <div className="add-experience-modal-footer">
          <button
            type="button"
            className="add-experience-modal-btn add-experience-modal-btn--discard"
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="button"
            className="add-experience-modal-btn add-experience-modal-btn--add"
            onClick={handleSubmit}
            disabled={!isFormValid}
            style={{
              opacity: !isFormValid ? 0.5 : 1,
              cursor: !isFormValid ? 'not-allowed' : 'pointer'
            }}
          >
            {isEditMode ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

